import React, { RefObject, useEffect, useLayoutEffect, useRef } from "react";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";
import nclude from "../assets/nclude-logo.svg";
import { normalizeChatMarkdown } from "../utils/normalizeChatMarkdown";

export type ChatMessage = {
  text: string;
  timestamp: string;
  sender: "user" | "bot";
  image?: { src: string; alt: string; hidden?: boolean };
  action?: { href: string; label: string };
};

type ChatProps = {
  chat: ChatMessage[];
  isStreaming?: boolean;
  assistantName?: string;
  announcementRef?: RefObject<HTMLDivElement>;
};

const isUpiPaymentUrl = (url: string) => /^upi:\/\/pay\?/i.test(url);

function transformChatUrl(url: string, key: string) {
  if (key === "href" && isUpiPaymentUrl(url)) return url;
  return defaultUrlTransform(url);
}

function ChatLink({ href, children, className }: {
  href?: string;
  children: React.ReactNode;
  className?: string;
}) {
  const destination = transformChatUrl(href || "", "href");
  if (!destination) return <span>{children}</span>;

  const isUpi = isUpiPaymentUrl(destination);
  return (
    <a href={destination} className={className} target={isUpi ? "_self" : "_blank"} rel={isUpi ? undefined : "noopener noreferrer"}>
      {children}
      <span className="visually-hidden">{isUpi ? " (opens your UPI app)" : " (opens in a new tab)"}</span>
    </a>
  );
}

function autoLinkMarkdown(text: string) {
  const urlRegex = /(^|\s)((?:https?:\/\/|www\.)[^\s<]+)/gi;
  return text.replace(urlRegex, (_match, prefix, url) => {
    const cleanUrl = url.replace(/[),.;!?]+$/, "");
    const punctuation = url.slice(cleanUrl.length);
    const href = cleanUrl.startsWith("www.") ? `https://${cleanUrl}` : cleanUrl;
    return `${prefix}[${cleanUrl}](${href})${punctuation}`;
  });
}

const Chat = ({
  chat,
  isStreaming = false,
  assistantName = "Assistant",
  announcementRef,
}: ChatProps) => {
  const endRef = useRef<HTMLDivElement>(null);
  const latestMessageRef = useRef<HTMLDivElement>(null);
  const previousMessageCountRef = useRef(0);

  const scrollLatestMessageToTop = () => {
    const latestMessage = latestMessageRef.current;
    const scrollContainer = latestMessage?.closest<HTMLElement>(".chat-window-container");
    if (!latestMessage || !scrollContainer) return;

    const containerRect = scrollContainer.getBoundingClientRect();
    const messageRect = latestMessage.getBoundingClientRect();
    scrollContainer.scrollTo({
      top: Math.max(0, scrollContainer.scrollTop + messageRect.top - containerRect.top - 12),
      behavior: "auto",
    });
  };

  useLayoutEffect(() => {
    const scrollContainer = endRef.current?.closest(".chat-window-container");
    if (!scrollContainer) return;

    const previousCount = previousMessageCountRef.current;
    const hasNewMessage = chat.length > previousCount;
    const latestMessage = chat[chat.length - 1];

    if (previousCount === 0) {
      scrollContainer.scrollTop = 0;
    } else if (hasNewMessage && latestMessage?.sender === "bot" && latestMessageRef.current) {
      const containerRect = scrollContainer.getBoundingClientRect();
      const messageRect = latestMessageRef.current.getBoundingClientRect();
      const messageTop = scrollContainer.scrollTop + messageRect.top - containerRect.top;
      scrollContainer.scrollTo({ top: Math.max(0, messageTop - 12), behavior: "smooth" });
    } else if (hasNewMessage) {
      scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior: "smooth" });
    }

    previousMessageCountRef.current = chat.length;
  }, [chat]);

  useEffect(() => {
    if (!announcementRef?.current || chat.length === 0) return;

    const latestMessage = chat[chat.length - 1];
    if (latestMessage.sender === "user") {
      announcementRef.current.textContent = "Message sent.";
      return;
    }

    if (isStreaming) return;

    const messageBody = latestMessageRef.current?.querySelector<HTMLElement>(".message-body");
    const responseText = (
      (latestMessage.image ? latestMessage.text : "") ||
      messageBody?.innerText ||
      messageBody?.textContent ||
      normalizeChatMarkdown(latestMessage.text)
    ).trim();

    announcementRef.current.textContent = responseText
      ? `${assistantName} response: ${responseText}`
      : `${assistantName} response is ready.`;
  }, [announcementRef, assistantName, chat, isStreaming]);

  return (
    <div className="conversation">
      {chat.map((message, index) => {
        const isAssistant = message.sender === "bot";
        const isActiveStream = isAssistant && isStreaming && index === chat.length - 1;
        const headingId = `conversation-message-heading-${index}`;
        const date = new Date(message.timestamp);
        const formattedTime = Number.isNaN(date.getTime())
          ? ""
          : date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

        return (
          <div
            ref={index === chat.length - 1 ? latestMessageRef : undefined}
            className={`conversation-message ${isAssistant ? "assistant-message" : "user-message"}`}
            key={`${message.timestamp}-${index}`}
            tabIndex={index === chat.length - 1 ? 0 : undefined}
            onFocus={index === chat.length - 1 ? scrollLatestMessageToTop : undefined}
          >
            <div className="message-inner">
              <div className="message-avatar" aria-hidden="true">
                {isAssistant ? <img src={nclude} alt="" /> : <span>Y</span>}
              </div>
              <div className="message-content">
                <div className="message-meta">
                  <h4
                    className="message-author"
                    id={headingId}
                  >
                    <span className="visually-hidden">
                      {isAssistant ? `${assistantName} said.` : "You said."}
                    </span>
                    <span aria-hidden="true">{isAssistant ? assistantName : "You"}</span>
                  </h4>
                  {formattedTime && <time dateTime={message.timestamp}>{formattedTime}</time>}
                </div>
                <div className="message-body">
                  {message.image ? (
                    message.image.hidden ? <p>Browser screenshot hidden.</p> : (
                      <img className="message-screenshot" src={message.image.src} alt={message.image.alt} loading="lazy" />
                    )
                  ) : <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    urlTransform={transformChatUrl}
                    components={{
                      a: ({ href, children }) => (
                        <ChatLink href={href}>{children}</ChatLink>
                      ),
                    }}
                  >
                    {autoLinkMarkdown(isAssistant ? normalizeChatMarkdown(message.text) : message.text)}
                  </ReactMarkdown>}
                  {message.action && (
                    <ChatLink className="message-action" href={message.action.href}>
                      {message.action.label}
                    </ChatLink>
                  )}
                  {isActiveStream && <span className="streaming-cursor" aria-hidden="true" />}
                </div>
              </div>
            </div>
          </div>
        );
      })}
      <div ref={endRef} aria-hidden="true" />
    </div>
  );
};

export default Chat;
